import { FormEvent, useState } from 'react'
import { authClient } from '../../../lib/auth-client'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'

export const ForgotPasswordRoute = () => {
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [sent, setSent] = useState(false)

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setLoading(true)

        const { error } = await authClient.requestPasswordReset({
            email,
            redirectTo: '/reset-password',
        })

        if (error) {
            toast.error(error.message || 'An error occurred')
            setLoading(false)
            return
        }

        setSent(true)
        setLoading(false)
    }

    return (
        <div className="flex min-h-screen">
            <div className="hidden lg:flex lg:w-1/2 bg-foreground items-center justify-center p-12">
                <div className="max-w-md">
                    <h2 className="text-3xl font-semibold tracking-tight text-background">
                        Trakkrr
                    </h2>
                    <p className="text-background/60 mt-3 text-sm leading-relaxed">
                        Monitor your fleet, manage devices, and keep operations
                        running with real-time insights.
                    </p>
                </div>
            </div>

            <div className="flex flex-1 flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-24 bg-background">
                <div className="w-full max-w-sm mx-auto">
                    {sent ? (
                        <div>
                            <div className="h-10 w-10 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mb-4">
                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                            </div>
                            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                                Check your email
                            </h1>
                            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                                If an account with that email exists, we've sent
                                a password reset link. Please check your inbox.
                            </p>
                            <Link
                                to="/login"
                                className="inline-block mt-6 text-xs text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Back to sign in
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                                    Forgot password
                                </h1>
                                <p className="text-sm text-muted-foreground mt-1.5">
                                    Enter your email and we'll send you a reset
                                    link.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-1.5">
                                    <label
                                        htmlFor="email"
                                        className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                                    >
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        required
                                        autoComplete="email"
                                        className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
                                        placeholder="you@company.com"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="h-10 w-full rounded-md bg-foreground text-background text-sm font-medium hover:bg-foreground/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    {loading ? 'Sending...' : 'Send reset link'}
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <Link
                                    to="/login"
                                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    Back to sign in
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
